import { Link } from 'react-router-dom'
import { 
  GraduationCap, 
  Users, 
  School,
  TrendingUp,
  DollarSign,
  Bell,
  ArrowLeft,
  BarChart3,
  Settings,
  Shield,
  CheckCircle,
  XCircle,
  Clock
} from 'lucide-react'

export default function SuperAdminDemo() {
  // Demo data
  const stats = {
    totalSchools: 156,
    activeSchools: 142,
    trialSchools: 28,
    totalUsers: 12450,
    totalRevenue: 48500,
    monthlyGrowth: 12.5
  }

  const schools = [
    { 
      name: 'Sunshine Primary School', 
      code: 'SCH123456', 
      status: 'active', 
      plan: 'Standard',
      users: 245,
      revenue: 1550,
      joined: 'Jan 15, 2024'
    },
    { 
      name: 'Greenfield Academy', 
      code: 'SCH789012', 
      status: 'trial', 
      plan: 'Trial',
      users: 89,
      revenue: 0,
      joined: 'May 1, 2024'
    },
    { 
      name: 'Oakwood High School', 
      code: 'SCH345678', 
      status: 'active', 
      plan: 'Premium',
      users: 512,
      revenue: 2850,
      joined: 'Mar 10, 2024'
    },
    { 
      name: 'Riverside Elementary', 
      code: 'SCH901234', 
      status: 'past_due', 
      plan: 'Standard',
      users: 178,
      revenue: 1550,
      joined: 'Feb 20, 2024'
    },
    { 
      name: 'Mountain View School', 
      code: 'SCH567890', 
      status: 'active', 
      plan: 'Standard',
      users: 320,
      revenue: 1550,
      joined: 'Apr 5, 2024'
    }
  ]

  const recentActivity = [
    { action: 'New school registered', school: 'Greenfield Academy', time: '2 hours ago', type: 'success' },
    { action: 'Payment received', school: 'Sunshine Primary', time: '4 hours ago', type: 'success' },
    { action: 'Trial ending soon', school: 'Riverside Elementary', time: '6 hours ago', type: 'warning' },
    { action: 'School suspended', school: 'Old Town Academy', time: '1 day ago', type: 'error' },
    { action: 'New user added', school: 'Oakwood High', time: '1 day ago', type: 'success' }
  ]

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Demo Banner */}
      <div className="bg-gray-900 text-white py-3 px-4">
        <div className="max-w-7xl mx-auto flex items-center justify-between">
          <div className="flex items-center gap-2">
            <span className="bg-purple-500/20 px-3 py-1 rounded-full text-sm font-medium border border-purple-500/30">Super Admin Demo</span>
            <span className="text-sm text-gray-300">Platform Overview Dashboard</span>
          </div>
          <Link to="/" className="text-sm hover:underline flex items-center gap-1 text-gray-300">
            <ArrowLeft className="w-4 h-4" /> Back to Home
          </Link>
        </div>
      </div>

      {/* Header */}
      <header className="bg-white border-b border-gray-200 sticky top-0 z-40">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center gap-2">
              <div className="w-10 h-10 bg-purple-600 rounded-xl flex items-center justify-center">
                <GraduationCap className="w-6 h-6 text-white" />
              </div>
              <span className="text-xl font-bold text-gray-900">SchoolConnect</span>
              <span className="bg-purple-100 text-purple-700 px-2 py-0.5 rounded text-xs font-medium ml-2">Super Admin</span>
            </div>
            <div className="flex items-center gap-4">
              <Bell className="w-5 h-5 text-gray-500" />
              <div className="w-8 h-8 bg-purple-100 rounded-full flex items-center justify-center">
                <span className="text-purple-700 font-semibold text-sm">SA</span>
              </div>
            </div>
          </div>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Welcome */}
        <div className="mb-8">
          <h1 className="text-2xl font-bold text-gray-900">Platform Overview</h1>
          <p className="text-gray-600">Manage all schools, users, and platform settings</p>
        </div>

        {/* Stats Grid */}
        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
          <div className="bg-white rounded-2xl p-6 shadow-sm border border-gray-100">
            <div className="flex items-center justify-between mb-4">
              <div className="w-12 h-12 bg-blue-50 rounded-xl flex items-center justify-center">
                <School className="w-6 h-6 text-blue-600" />
              </div>
              <span className="text-green-600 text-sm font-medium flex items-center">
                <TrendingUp className="w-4 h-4 mr-1" /> +{stats.monthlyGrowth}%
              </span>
            </div>
            <p className="text-3xl font-bold text-gray-900">{stats.totalSchools}</p>
            <p className="text-sm text-gray-500">Total Schools</p>
          </div>

          <div className="bg-white rounded-2xl p-6 shadow-sm border border-gray-100">
            <div className="flex items-center justify-between mb-4">
              <div className="w-12 h-12 bg-green-50 rounded-xl flex items-center justify-center">
                <Users className="w-6 h-6 text-green-600" />
              </div>
              <span className="text-green-600 text-sm font-medium">Active</span>
            </div>
            <p className="text-3xl font-bold text-gray-900">{stats.totalUsers.toLocaleString()}</p>
            <p className="text-sm text-gray-500">Total Users</p>
          </div>

          <div className="bg-white rounded-2xl p-6 shadow-sm border border-gray-100">
            <div className="flex items-center justify-between mb-4">
              <div className="w-12 h-12 bg-purple-50 rounded-xl flex items-center justify-center">
                <DollarSign className="w-6 h-6 text-purple-600" />
              </div>
              <span className="text-green-600 text-sm font-medium">This Month</span>
            </div>
            <p className="text-3xl font-bold text-gray-900">${stats.totalRevenue.toLocaleString()}</p>
            <p className="text-sm text-gray-500">Monthly Revenue</p>
          </div>

          <div className="bg-white rounded-2xl p-6 shadow-sm border border-gray-100">
            <div className="flex items-center justify-between mb-4">
              <div className="w-12 h-12 bg-orange-50 rounded-xl flex items-center justify-center">
                <Clock className="w-6 h-6 text-orange-600" />
              </div>
              <span className="text-orange-600 text-sm font-medium">Trial</span>
            </div>
            <p className="text-3xl font-bold text-gray-900">{stats.trialSchools}</p>
            <p className="text-sm text-gray-500">Schools in Trial</p>
          </div>
        </div>

        <div className="grid lg:grid-cols-3 gap-8">
          {/* Schools List */}
          <div className="lg:col-span-2">
            <div className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden">
              <div className="p-6 border-b border-gray-100">
                <div className="flex items-center justify-between">
                  <h2 className="text-lg font-semibold text-gray-900">Recent Schools</h2>
                  <button className="text-purple-600 text-sm font-medium hover:underline">View All</button>
                </div>
              </div>
              <div className="divide-y divide-gray-100">
                {schools.map((school, idx) => (
                  <div key={idx} className="p-6 hover:bg-gray-50 transition-colors">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-4">
                        <div className="w-10 h-10 bg-gradient-to-br from-blue-400 to-blue-600 rounded-xl flex items-center justify-center">
                          <School className="w-5 h-5 text-white" />
                        </div>
                        <div>
                          <p className="font-semibold text-gray-900">{school.name}</p>
                          <p className="text-sm text-gray-500">{school.code} • {school.joined}</p>
                        </div>
                      </div>
                      <div className="text-right">
                        <span className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-medium ${
                          school.status === 'active' ? 'bg-green-100 text-green-700' :
                          school.status === 'trial' ? 'bg-blue-100 text-blue-700' :
                          school.status === 'past_due' ? 'bg-red-100 text-red-700' :
                          'bg-gray-100 text-gray-700'
                        }`}>
                          {school.status === 'active' && <CheckCircle className="w-3 h-3 mr-1" />}
                          {school.status === 'trial' && <Clock className="w-3 h-3 mr-1" />}
                          {school.status === 'past_due' && <XCircle className="w-3 h-3 mr-1" />}
                          {school.status.charAt(0).toUpperCase() + school.status.slice(1)}
                        </span>
                        <p className="text-sm text-gray-600 mt-1">{school.users} users</p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Sidebar */}
          <div className="space-y-6">
            {/* Quick Actions */}
            <div className="bg-white rounded-2xl p-6 shadow-sm border border-gray-100">
              <h2 className="text-lg font-semibold text-gray-900 mb-4">Quick Actions</h2>
              <div className="space-y-3">
                <button className="w-full p-3 bg-purple-50 rounded-xl text-left hover:bg-purple-100 transition-colors">
                  <School className="w-5 h-5 text-purple-600 mb-1" />
                  <p className="font-medium text-gray-900 text-sm">Add New School</p>
                </button>
                <button className="w-full p-3 bg-blue-50 rounded-xl text-left hover:bg-blue-100 transition-colors">
                  <Users className="w-5 h-5 text-blue-600 mb-1" />
                  <p className="font-medium text-gray-900 text-sm">Manage Users</p>
                </button>
                <button className="w-full p-3 bg-green-50 rounded-xl text-left hover:bg-green-100 transition-colors">
                  <BarChart3 className="w-5 h-5 text-green-600 mb-1" />
                  <p className="font-medium text-gray-900 text-sm">View Analytics</p>
                </button>
                <button className="w-full p-3 bg-gray-50 rounded-xl text-left hover:bg-gray-100 transition-colors">
                  <Settings className="w-5 h-5 text-gray-600 mb-1" />
                  <p className="font-medium text-gray-900 text-sm">Platform Settings</p>
                </button>
              </div>
            </div>

            {/* Recent Activity */}
            <div className="bg-white rounded-2xl p-6 shadow-sm border border-gray-100">
              <h2 className="text-lg font-semibold text-gray-900 mb-4">Recent Activity</h2>
              <div className="space-y-4">
                {recentActivity.map((activity, idx) => (
                  <div key={idx} className="flex items-start gap-3">
                    <div className={`w-2 h-2 rounded-full mt-2 flex-shrink-0 ${
                      activity.type === 'success' ? 'bg-green-500' :
                      activity.type === 'warning' ? 'bg-orange-500' :
                      'bg-red-500'
                    }`} />
                    <div>
                      <p className="text-sm font-medium text-gray-900">{activity.action}</p>
                      <p className="text-xs text-gray-500">{activity.school}</p>
                      <p className="text-xs text-gray-400">{activity.time}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* System Status */}
            <div className="bg-white rounded-2xl p-6 shadow-sm border border-gray-100">
              <h2 className="text-lg font-semibold text-gray-900 mb-4">System Status</h2>
              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <Shield className="w-4 h-4 text-green-600" />
                    <span className="text-sm text-gray-700">Security</span>
                  </div>
                  <span className="text-xs text-green-600 font-medium">Operational</span>
                </div>
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <BarChart3 className="w-4 h-4 text-green-600" />
                    <span className="text-sm text-gray-700">Analytics</span>
                  </div>
                  <span className="text-xs text-green-600 font-medium">Operational</span>
                </div>
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <Users className="w-4 h-4 text-green-600" />
                    <span className="text-sm text-gray-700">User Auth</span>
                  </div>
                  <span className="text-xs text-green-600 font-medium">Operational</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* CTA */}
      <div className="bg-gray-900 py-12 mt-12">
        <div className="max-w-4xl mx-auto px-4 text-center">
          <h2 className="text-2xl font-bold text-white mb-4">Ready to manage your school platform?</h2>
          <p className="text-gray-300 mb-6">Start your 14-day free trial and get full Super Admin access</p>
          <div className="flex flex-wrap justify-center gap-4">
            <Link to="/register-school" className="bg-purple-600 hover:bg-purple-700 text-white px-8 py-3 rounded-full font-semibold transition-colors">
              Start 14-Day Free Trial
            </Link>
            <Link to="/demo/teacher" className="bg-white/10 text-white px-8 py-3 rounded-full font-semibold hover:bg-white/20 transition-colors">
              See Teacher View
            </Link>
          </div>
        </div>
      </div>
    </div>
  )
}
